import { useEffect, useState, useCallback } from 'react';
import { Award, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CertificationsSection() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Embla Carousel setup for horizontal scrolling
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true, 
      align: 'start',
      slidesToScroll: 1,
      containScroll: 'trimSnaps'
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/certificates`);
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't render if no certificates
  if (loading || certificates.length === 0) {
    return null;
  }

  return (
    <section data-testid="certifications-section" className="bg-gradient-to-br from-slate-50 to-slate-100 py-16 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 border border-blue-200 mb-4">
            <Award size={18} className="text-blue-600 mr-2" />
            <span className="text-blue-700 text-sm font-medium">Certified & Registered</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Our Certifications & Registrations
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            We are proud to be certified and registered with leading authorities
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          {certificates.length > 3 && (
            <>
              <button
                onClick={scrollPrev}
                data-testid="cert-carousel-prev"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg hover:bg-blue-50 border border-slate-200 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 -ml-4"
                aria-label="Previous certificate"
              >
                <ChevronLeft size={20} className="text-slate-600" />
              </button>
              <button
                onClick={scrollNext}
                data-testid="cert-carousel-next"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white shadow-lg hover:bg-blue-50 border border-slate-200 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 -mr-4"
                aria-label="Next certificate"
              >
                <ChevronRight size={20} className="text-slate-600" />
              </button>
            </>
          )}

          {/* Embla Carousel */}
          <div className="embla overflow-hidden mx-6" ref={emblaRef}>
            <div className="embla__container flex gap-6">
              {certificates.map((cert, index) => (
                <div
                  key={cert.id}
                  data-testid={`certificate-card-${index}`}
                  className="embla__slide flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0"
                >
                  <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 h-full border border-slate-100 hover:border-blue-200 group">
                    {/* Certificate Icon/Image */}
                    <div className="flex items-center justify-center mb-4">
                      {cert.image_url ? (
                        <img 
                          src={cert.image_url} 
                          alt={cert.title}
                          className="h-16 w-auto object-contain"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Award size={32} className="text-white" />
                        </div>
                      )}
                    </div>

                    {/* Certificate Details */}
                    <div className="text-center">
                      <h3 className="font-bold text-lg text-slate-900 mb-2 group-hover:text-blue-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {cert.title}
                      </h3>
                      
                      {cert.description && (
                        <p className="text-sm text-slate-600 mb-3">
                          {cert.description}
                        </p>
                      )}

                      {cert.registration_number && (
                        <div className="inline-block px-3 py-1 bg-blue-50 rounded-full mb-2">
                          <span className="text-xs font-medium text-blue-700">
                            Reg. No: {cert.registration_number}
                          </span>
                        </div>
                      )}

                      {cert.issuing_authority && (
                        <p className="text-xs text-slate-500">
                          Issued by: {cert.issuing_authority}
                        </p>
                      )}

                      {(cert.valid_from || cert.valid_until) && (
                        <p className="text-xs text-slate-400 mt-2">
                          {cert.valid_from && `From: ${cert.valid_from}`}
                          {cert.valid_from && cert.valid_until && ' | '}
                          {cert.valid_until && `Until: ${cert.valid_until}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
